import { useState } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2 } from "lucide-react";
import { useCompleteSession } from "@/lib/sessionQueries";
import { useJourneyDraft, useJourneyStatePreview } from "@/lib/journeyQueries";
import { useLibraryCatalog } from "@/lib/practiceQueries";
import { HOURS, MINUTES, toLocalDate, formatInTimezone } from "@/lib/localDateTime";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { JourneyRail } from "../JourneyRail";

const LAYER_ACTION_OPTIONS = [
  { value: "none", label: "No change" },
  { value: "introduced", label: "Introduced" },
  { value: "unlocked", label: "Unlocked" },
  { value: "current", label: "Set as current" },
  { value: "revisit", label: "Mark for revisit" },
];

function newIdempotencyKey() {
  return `complete-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Master v5 Prompt 18 — Complete Session. One transactional/idempotent
 * workflow (the SAME idempotency_key is reused across retries of this
 * exact submission — never regenerated on error, only after success or
 * a fresh open). Ending/leaving the Room is a SEPARATE, unrelated action
 * — this is the only place that ever sets a Session to 'completed'.
 */
export default function CompleteSessionWizard({ workspaceId, session, accessType, onClose, onCompleted }) {
  const [idemKey] = useState(newIdempotencyKey);
  const [whatWorked, setWhatWorked] = useState("");
  const [observations, setObservations] = useState("");
  const [coachPrivateNotes, setCoachPrivateNotes] = useState("");
  const [clientRecap, setClientRecap] = useState("");

  const [addFeedback, setAddFeedback] = useState(false);
  const [feedbackFocus, setFeedbackFocus] = useState("");
  const [priorities, setPriorities] = useState([{ observation: "", impact: "", suggestion: "" }]);
  const [publishFeedback, setPublishFeedback] = useState(false);

  const { data: journeyDraft } = useJourneyDraft(workspaceId);
  const [layerActions, setLayerActions] = useState({});
  const statePreview = useJourneyStatePreview(workspaceId);

  const [addAssignment, setAddAssignment] = useState(false);
  const { data: catalog } = useLibraryCatalog(workspaceId);
  const [assignmentLibId, setAssignmentLibId] = useState("");
  const [assignmentTarget, setAssignmentTarget] = useState("this_client"); // this_client | whole_team

  const [commitmentText, setCommitmentText] = useState("");
  const [nextFocus, setNextFocus] = useState("");
  const [proposeNext, setProposeNext] = useState(false);
  const [nextSlot, setNextSlot] = useState({ date: "", hour: "", minute: "" });

  const completeSession = useCompleteSession(workspaceId);

  const stages = journeyDraft?.stage_specs || [];

  function updatePriority(i, field, value) {
    setPriorities((prev) => prev.map((p, idx) => (idx === i ? { ...p, [field]: value } : p)));
  }

  async function handlePreviewJourney() {
    const overrides = Object.entries(layerActions).filter(([, v]) => v && v !== "none").map(([stage_key, action]) => ({ stage_key, action }));
    if (overrides.length === 0) {
      toast.error("Choose at least one stage change to preview");
      return;
    }
    try {
      await statePreview.mutateAsync({ targetMembershipId: session.owner_member_id, stateOverrides: overrides });
    } catch (err) {
      toast.error(err.body?.detail || "Couldn't build the preview");
    }
  }

  async function handleSubmit() {
    if (!whatWorked.trim() || !observations.trim()) {
      toast.error("What worked and Observations are required");
      return;
    }
    const layer_actions = Object.entries(layerActions).filter(([, v]) => v && v !== "none").map(([stage_key, action]) => ({ stage_key, action }));

    const feedback_items = [];
    if (addFeedback) {
      const cleanPriorities = priorities.filter((p) => p.observation.trim()).map((p) => ({
        observation: p.observation, impact: p.impact || null, suggestion: p.suggestion || null,
      }));
      if (cleanPriorities.length === 0) {
        toast.error("Add at least one Feedback priority, or turn off Feedback");
        return;
      }
      feedback_items.push({
        owner_member_id: session.owner_member_id, focus: feedbackFocus || "This session", priorities: cleanPriorities,
        evidence: [], publish: publishFeedback,
        audience_selection: accessType === "team" ? { choice: "individual_member", target_membership_ids: [session.owner_member_id] } : null,
      });
    }

    const assignments = [];
    if (addAssignment && assignmentLibId) {
      const item = (catalog?.items || []).find((c) => c.lib_id === assignmentLibId);
      if (item) {
        assignments.push({
          owner_member_id: assignmentTarget === "whole_team" ? null : session.owner_member_id,
          layer_key: item.primary_layer_key || "structure", lib_id: item.lib_id, libv_id: item.libv_id,
          renderer_key: "content_viewer", title_snapshot: item.title,
        });
      }
    }

    const nextSlotDate = toLocalDate(nextSlot);
    const next_time_proposal = proposeNext && nextSlotDate ? { slots: [{ starts_at: nextSlotDate.toISOString(), duration_minutes: 60 }], note: null } : null;

    try {
      await completeSession.mutateAsync({
        sesId: session.ses_id,
        payload: {
          idempotency_key: idemKey, what_worked: whatWorked, observations, coach_private_notes: coachPrivateNotes || null,
          client_visible_recap: clientRecap || null, feedback_items, layer_actions, assignments,
          commitment_text: commitmentText || null, next_focus: nextFocus || null, next_time_proposal,
        },
      });
      toast.success("Session completed");
      onCompleted?.();
      onClose();
    } catch (err) {
      toast.error(err.body?.detail || "Couldn't complete this Session — you can safely retry");
    }
  }

  return (
    <div className="fixed inset-0 bg-ink/40 flex items-center justify-center z-50 p-4" data-testid="complete-session-wizard-modal">
      <div className="bg-paper rounded-xl border border-ink/10 max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl text-ink">Complete Session — {session.title}</h2>
          <button onClick={onClose} data-testid="complete-session-close-btn"><X size={20} className="text-ink/50" /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-ink-soft">What worked *</label>
            <Textarea value={whatWorked} onChange={(e) => setWhatWorked(e.target.value)} data-testid="complete-session-what-worked-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-ink-soft">Observations *</label>
            <Textarea value={observations} onChange={(e) => setObservations(e.target.value)} data-testid="complete-session-observations-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-ink-soft">Coach-private notes</label>
            <Textarea value={coachPrivateNotes} onChange={(e) => setCoachPrivateNotes(e.target.value)} data-testid="complete-session-private-notes-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-ink-soft">Client-visible recap</label>
            <Textarea value={clientRecap} onChange={(e) => setClientRecap(e.target.value)} data-testid="complete-session-client-recap-input" />
          </div>
        </div>

        <div className="border-t border-ink/10 pt-4">
          <label className="flex items-center gap-2 text-sm font-medium text-ink">
            <input type="checkbox" checked={addFeedback} onChange={(e) => setAddFeedback(e.target.checked)} data-testid="complete-session-add-feedback-toggle" />
            Add Feedback
          </label>
          {addFeedback && (
            <div className="mt-2 space-y-2 pl-1">
              <Input placeholder="Focus (e.g. Opening)" value={feedbackFocus} onChange={(e) => setFeedbackFocus(e.target.value)} data-testid="complete-session-feedback-focus-input" />
              {priorities.map((p, i) => (
                <div key={i} className="grid grid-cols-3 gap-2" data-testid={`complete-session-feedback-priority-row-${i}`}>
                  <Input placeholder="Observation" value={p.observation} onChange={(e) => updatePriority(i, "observation", e.target.value)} data-testid={`complete-session-priority-observation-${i}`} />
                  <Input placeholder="Impact" value={p.impact} onChange={(e) => updatePriority(i, "impact", e.target.value)} data-testid={`complete-session-priority-impact-${i}`} />
                  <Input placeholder="Suggestion" value={p.suggestion} onChange={(e) => updatePriority(i, "suggestion", e.target.value)} data-testid={`complete-session-priority-suggestion-${i}`} />
                </div>
              ))}
              {priorities.length < 3 && (
                <button onClick={() => setPriorities((prev) => [...prev, { observation: "", impact: "", suggestion: "" }])} className="text-xs text-terracotta flex items-center gap-1" data-testid="complete-session-add-priority-btn">
                  <Plus size={12} /> Add priority
                </button>
              )}
              <label className="flex items-center gap-2 text-xs text-ink-soft">
                <input type="checkbox" checked={publishFeedback} onChange={(e) => setPublishFeedback(e.target.checked)} data-testid="complete-session-publish-feedback-toggle" />
                Publish now (otherwise saved as a draft)
              </label>
            </div>
          )}
        </div>

        <div className="border-t border-ink/10 pt-4">
          <h3 className="text-sm font-medium text-ink mb-2">Journey/layers — introduced, unlocked, current or revisit</h3>
          {stages.length === 0 && <p className="text-xs text-ink-soft">No published journey yet for this client.</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              {stages.map((s) => (
                <div key={s.stage_key} className="flex items-center justify-between gap-2" data-testid={`complete-session-layer-row-${s.stage_key}`}>
                  <span className="text-sm text-ink">{s.label}</span>
                  <Select value={layerActions[s.stage_key] || "none"} onValueChange={(v) => setLayerActions((prev) => ({ ...prev, [s.stage_key]: v }))}>
                    <SelectTrigger className="h-8 w-40 text-xs" data-testid={`complete-session-layer-action-${s.stage_key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{LAYER_ACTION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
              {stages.length > 0 && (
                <button onClick={handlePreviewJourney} disabled={statePreview.isPending} className="text-xs font-medium border border-ink/15 rounded-full px-3 py-1.5 hover:bg-ink/5" data-testid="complete-session-preview-journey-btn">
                  {statePreview.isPending ? "Previewing…" : "Preview client Home"}
                </button>
              )}
            </div>
            <div>
              {statePreview.data ? (
                <div className="border border-ink/10 bg-white/40 rounded-xl p-3" data-testid="complete-session-journey-preview-rail">
                  <JourneyRail nodes={statePreview.data.nodes} testIdPrefix="complete-session-journey-preview" />
                </div>
              ) : (
                <p className="text-xs text-ink-soft" data-testid="complete-session-journey-preview-empty">Choose stage changes and click preview to see exactly what the client's Home will show before you publish.</p>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-ink/10 pt-4">
          <label className="flex items-center gap-2 text-sm font-medium text-ink">
            <input type="checkbox" checked={addAssignment} onChange={(e) => setAddAssignment(e.target.checked)} data-testid="complete-session-add-assignment-toggle" />
            Assign practice
          </label>
          {addAssignment && (
            <div className="mt-2 flex items-center gap-2">
              <Select value={assignmentLibId} onValueChange={setAssignmentLibId}>
                <SelectTrigger className="h-8 text-xs flex-1" data-testid="complete-session-assignment-item-select"><SelectValue placeholder="Choose content…" /></SelectTrigger>
                <SelectContent>{(catalog?.items || []).map((c) => <SelectItem key={c.lib_id} value={c.lib_id}>{c.title}</SelectItem>)}</SelectContent>
              </Select>
              {accessType === "team" && (
                <Select value={assignmentTarget} onValueChange={setAssignmentTarget}>
                  <SelectTrigger className="h-8 w-36 text-xs" data-testid="complete-session-assignment-target-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="this_client">This client</SelectItem>
                    <SelectItem value="whole_team">Whole team</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-ink/10 pt-4">
          <div>
            <label className="text-xs font-medium text-ink-soft">Commitment</label>
            <Textarea value={commitmentText} onChange={(e) => setCommitmentText(e.target.value)} data-testid="complete-session-commitment-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-ink-soft">Next focus</label>
            <Textarea value={nextFocus} onChange={(e) => setNextFocus(e.target.value)} data-testid="complete-session-next-focus-input" />
          </div>
        </div>

        <div className="border-t border-ink/10 pt-4">
          <label className="flex items-center gap-2 text-sm font-medium text-ink">
            <input type="checkbox" checked={proposeNext} onChange={(e) => setProposeNext(e.target.checked)} data-testid="complete-session-propose-next-toggle" />
            Propose the next session time now
          </label>
          {proposeNext && (
            <div className="mt-2 flex items-center gap-2">
              <Input type="date" value={nextSlot.date} onChange={(e) => setNextSlot((s) => ({ ...s, date: e.target.value }))} className="w-40" data-testid="complete-session-next-slot-date" />
              <select value={nextSlot.hour} onChange={(e) => setNextSlot((s) => ({ ...s, hour: e.target.value }))} className="border border-input rounded-md h-9 px-2 text-sm bg-background" data-testid="complete-session-next-slot-hour">
                <option value="" disabled>HH</option>
                {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
              <span className="text-ink-soft">:</span>
              <select value={nextSlot.minute} onChange={(e) => setNextSlot((s) => ({ ...s, minute: e.target.value }))} className="border border-input rounded-md h-9 px-2 text-sm bg-background" data-testid="complete-session-next-slot-minute">
                <option value="" disabled>MM</option>
                {MINUTES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              {toLocalDate(nextSlot) && (
                <p className="text-xs text-ink-soft" data-testid="complete-session-next-slot-preview">= {formatInTimezone(toLocalDate(nextSlot), "America/Sao_Paulo")} (São Paulo)</p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-paper">
          <button onClick={onClose} className="text-sm text-ink/60 px-4 py-2" data-testid="complete-session-cancel-btn">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={completeSession.isPending}
            className="bg-ink text-paper text-sm font-medium px-5 py-2.5 rounded-md hover:bg-ink/85 disabled:opacity-50"
            data-testid="complete-session-submit-btn"
          >
            {completeSession.isPending ? "Completing…" : "Complete Session"}
          </button>
        </div>
      </div>
    </div>
  );
}
